import { Box, CircularProgress, debounce, Dialog, DialogContent, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { lazy, Suspense, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Context } from '../../../../App';

import CommonDialog from '../../../../common/CommonDialog';
import { clearAllPaths, getPreviousPath } from '../../../../components/history';
import { SHOP_CREATED, SHOP_IMPORTED, USER_TYPES } from '../../../../config/dataService/constants';
import { getAllShopServiceApi } from '../../../../redux/Account/ServiceOffered/action';
import { customerDetail, importedCustomerDetail } from '../../../../redux/Customer/action';
import { CustomersListBasedOnType } from '../../../../redux/Invoice/action';
import { vehicleDetails } from '../../../../redux/Vehicles/action';
import { mechanicDetailsList } from '../../../../redux/appointment/action';
import { getShopClosingTimeApi } from '../../../../services/business/appointment/appointment.api';
import { appointmentKeys, useCreateAppointment } from '../../../../services/business/appointment/appointment.hooks';
import { customerKeys } from '../../../../services/business/customer/customer.hooks';
import { useGetShopConfig } from '../../../../services/core/settings';
import { useGetAvailableDates, useGetSlotsWithDisabled, useGetAppointmentConfirmedNotificationStatus } from '../../../../services/core/settings/settings.hooks';
import { isTimeWithinBusinessHours } from '../../../../utility/helpers/commonFunction';
import { resolveSelectedVehicleId } from '../../../../utility/vehicleSelection';
import AdditionalDetailsAccordion from './AdditionalDetailsAccordion';
import AppointmentDetailsAccordion from './AppointmentDetailsAccordion';
import Footer from './Footer';
import Header from './Header';
import ServicesSection from './ServicesSection';

// Lazy load modals
const AddCustomerModalV4 = lazy(() => import('../../../../common/AddCustomerModalV4'));
const VehicleModalV4 = lazy(() => import('../../../../common/VehicleModalV4'));

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
const localTimezone = dayjs.tz.guess();

const NewAppointmentV4 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id, type } = useParams();
    const dispatch = useDispatch();
    const { isOpenSideBar } = useContext(Context);
    const theme = useTheme();
    const isBelow1440 = useMediaQuery(theme.breakpoints.down(1440));

    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
        setError,
        getValues,
        clearErrors,
        trigger,
        reset,
    } = useForm();

    // State variables - matching NewAppointment.jsx structure
    const [customerDetails, setCustomerDetails] = useState([]);
    const [customerData, setCustomerData] = useState(null);
    const [customerListLoader, setCustomerListLoader] = useState(false);
    const [customerTypeData, setCustomerTypeData] = useState(['shop_created', 'apex_auto_app', 'shop_imported']);
    const [customerSearch, setCustomerSearch] = useState('');
    const [state, setState] = useState({
        shop_created: true,
        shop_imported: true,
        apex_auto_app: false,
    });
    const [open, setOpen] = React.useState(false);

    const [customerCreatedFrom, setCustomerCreatedFrom] = useState('');
    const [openAddCustomerModal, setOpenAddCustomerModal] = useState(false);
    const isCustomerDeleteRef = useRef(false);

    const [openCustomerInfo, setOpenCustomerInfo] = useState(false);
    const [skipConfirmation, setSkipConfirmation] = useState(false);

    const [vehicleList, setVehicleList] = useState([]);
    const [vehicleData, setVehicleData] = useState('');
    const [vehicleLoader, setVehicleLoader] = useState(false);
    const skipVehicleDetailRef = useRef(false);

    // const [vehicleSourceData, setVehicleSourceData] = useState('');
    //  const [vehicleInfoOpen, setVehicleInfoOpen] = useState(false);
    const [newVehicleCreatedId, setVehicleCreatedId] = useState('');
    const [openVehicleModal, setOpenVehicleModal] = useState(false);
    const [editVehicleData, setEditVehicleData] = useState(null);
    const [isEditCustomer, setIsEditCustomer] = useState(false);

    const [dropOffDate, setDropOffDate] = useState(dayjs());
    const [pickUpDate, setPickUpDate] = useState(null);
    const [isFirstSelection, setIsFirstSelection] = useState(true);
    const cancelTokenSourceRef = useRef(null);
    const bypassBusinessHoursCheckRef = useRef(false);
    const datePickerWasUsedRef = useRef(false); // Track if user opened and saved from date picker

    const [customerOnSite, setCustomerOnSite] = useState('');
    const [servicePageData, setServicePageData] = useState({
        totalpages: '',
        currentPage: '',
    });

    // Mechanic/Assignee state
    const [mechanicDetail, setMechanicDetail] = useState('');
    const [mechanicData, setMechanicData] = useState('');
    const [assigneeLoader, setAssigneeLoader] = useState(false);

    // Loader states
    const [showLoader, setShowLoader] = useState(false);
    const [showIdLoader, setShowIdLoader] = useState(true);
    const [checkChangeLoader, setCheckChangeLoader] = useState(false);

    // Service list state - matching NewAppointment.jsx structure
    const [serviceList, setServiceList] = useState([{ _id: 'select' }]);

    const [openOutsideBusinessHoursDialog, setOpenOutsideBusinessHoursDialog] = useState(false);
    const [triggerOpenDatePicker, setTriggerOpenDatePicker] = useState(0);

    // Pagination
    const [page, setPage] = useState(1);
    const limits = 50;

    const SHOP_ID = JSON.parse(localStorage.getItem('apex-saas_details'))?._id;

    // Extract year and month for available dates API
    const queryYear = useMemo(() => dropOffDate.year(), [dropOffDate]);
    const currentMonthNumber = useMemo(() => dropOffDate.month() + 1, [dropOffDate]);

    // Get available dates for the current month/year
    const availableDatesQuery = useGetAvailableDates(
        { shopId: SHOP_ID, year: queryYear.toString(), month: currentMonthNumber.toString(), includeAppointmentCount: true },
        { enabled: !!queryYear && !!currentMonthNumber && !!SHOP_ID }
    );

    // Process available dates to create a Set of available date strings (YYYY-MM-DD format)
    // Only dates with isAvailable: true are included in the Set
    // Also create a Map of appointment counts for each date
    // Also find first available date in current month when month changes (not on initial load)
    const availableDatesSet = useMemo(() => {
        if (availableDatesQuery.isSuccess && availableDatesQuery.data && Array.isArray(availableDatesQuery.data)) {
            const datesSet = new Set(
                availableDatesQuery.data.filter((dateObj) => dateObj.isAvailable).map((dateObj) => dayjs(dateObj.date).format('YYYY-MM-DD'))
            );
            return datesSet;
        }
        return new Set();
    }, [availableDatesQuery.isSuccess, availableDatesQuery.data, queryYear, currentMonthNumber]);

    // Check if selected date is available (isAvailable: true)
    const isSelectedDateAvailable = useMemo(() => {
        if (!dropOffDate || !availableDatesQuery.isSuccess || availableDatesSet.size === 0) return false;
        const dateStr = dropOffDate.format('YYYY-MM-DD');
        return availableDatesSet.has(dateStr);
    }, [dropOffDate, availableDatesQuery.isSuccess, availableDatesSet]);

    const slotsQuery = useGetSlotsWithDisabled(
        {
            shopId: SHOP_ID,
            date: dropOffDate ? dayjs(dropOffDate).format('YYYY-MM-DD') : null,
            includeAppointmentCount: true,
        },
        { enabled: !!dropOffDate && isSelectedDateAvailable }
    );

    // Get shop config
    const { data: shopConfigData, isLoading: isShopConfigLoading, error: shopConfigError } = useGetShopConfig();

    // Get appointmentConfirmed notification status from customer notification templates API
    const { isEnabled: isAppointmentConfirmedEnabled, isSuccess: isNotificationConfigSuccess } = useGetAppointmentConfirmedNotificationStatus();

    // Hydrate default skipConfirmation toggle state based on appointmentConfirmed isEnabled status
    useEffect(() => {
        if (isNotificationConfigSuccess && !id) {
            setSkipConfirmation(!isAppointmentConfirmedEnabled);
        }
    }, [isNotificationConfigSuccess, isAppointmentConfirmedEnabled, id]);

    //Slot to show the start time and endtime of the shop logic
    const processedSlotsFull = useMemo(() => {
        // Don't show slots if date is not available (isAvailable: false)
        if (!dropOffDate || !isSelectedDateAvailable) {
            return [];
        }
        if (!slotsQuery?.data || !Array.isArray(slotsQuery.data) || availableDatesQuery.isLoading || slotsQuery.isLoading) {
            return [];
        }

        const timeSlots = slotsQuery.data;
        const now = new Date();
        const selectedDateStr = dropOffDate.format('YYYY-MM-DD');
        const todayStr = dayjs().format('YYYY-MM-DD');
        const isToday = selectedDateStr === todayStr;

        return timeSlots.map((slot) => {
            const startDateTime = dayjs(slot.startDateTime);
            const timeLabel = startDateTime.format('h:mm A');
            const endTimeLabel = dayjs(slot.endDateTime).format('h:mm A');
            return {
                timeLabel,
                endTimeLabel,
                fullSlot: slot,
                isManuallyDisabled: !slot.availability,
                appointmentCount: slot.appointmentCount,
            };
        });
    }, [slotsQuery?.data, dropOffDate, availableDatesQuery.isLoading, slotsQuery.isLoading, isSelectedDateAvailable]);

    // Process slots from API to show the time slot
    const processedSlots = useMemo(() => {
        // Don't show slots if date is not available (isAvailable: false)
        if (!dropOffDate || !isSelectedDateAvailable) {
            return [];
        }
        if (!slotsQuery?.data || !Array.isArray(slotsQuery.data) || availableDatesQuery.isLoading || slotsQuery.isLoading) {
            return [];
        }

        const timeSlots = slotsQuery.data;
        const now = new Date();
        const selectedDateStr = dropOffDate.format('YYYY-MM-DD');
        const todayStr = dayjs().format('YYYY-MM-DD');
        const isToday = selectedDateStr === todayStr;

        return timeSlots
            .filter((slot) => {
                // Filter out past slots only for display (if selected date is today)
                if (isToday) {
                    const startDateTime = dayjs(slot.startDateTime);
                    const slotTime = startDateTime.format('HH:mm');
                    const combinedDateTimeStr = `${selectedDateStr} ${slotTime}`;
                    const combinedDateTime = dayjs(combinedDateTimeStr, 'YYYY-MM-DD HH:mm').toDate();
                    return combinedDateTime > now;
                }
                return true;
            })
            .map((slot) => {
                const startDateTime = dayjs(slot.startDateTime);
                const timeLabel = startDateTime.format('h:mm A');
                const endTimeLabel = dayjs(slot.endDateTime).format('h:mm A');
                return {
                    timeLabel,
                    endTimeLabel,
                    fullSlot: slot,
                    isManuallyDisabled: !slot.availability,
                    appointmentCount: slot.appointmentCount,
                };
            });
    }, [slotsQuery?.data, dropOffDate, availableDatesQuery.isLoading, slotsQuery.isLoading, isSelectedDateAvailable]);

    // Get business hours info for dialog message
    const businessHoursInfo = useMemo(() => {
        const selectedTime = dropOffDate ? dropOffDate.format('h:mm A') : '';
        const businessDay = dropOffDate ? dropOffDate.format('dddd') : ''; // e.g., "Monday", "Tuesday"
        const openTime = processedSlotsFull.length > 0 ? processedSlotsFull[0]?.timeLabel : '';
        const closeTime = processedSlotsFull.length > 0 ? processedSlotsFull[processedSlotsFull.length - 1]?.endTimeLabel : '';

        return {
            selectedTime,
            businessDay,
            openTime,
            closeTime,
        };
    }, [dropOffDate, processedSlotsFull]);

    // Customer list fetching function
    const customerList = async (pageNum, limitsNum, createId) => {
        try {
            if (showIdLoader && id) {
                setShowLoader(true);
                setShowIdLoader(false);
            }
            setCustomerListLoader(true);
            let data;

            if (customerTypeData.length > 0) {
                data = await dispatch(CustomersListBasedOnType(customerSearch, customerTypeData, pageNum, limitsNum));
                if (data?.data) {
                    // Append data when paginating, replace when it's first page or search
                    if (pageNum > 1) {
                        setCustomerDetails((prev) => [...prev, ...data.data]);
                    } else {
                        setCustomerDetails(data.data);
                    }

                    if (createId && createId !== '') {
                        const foundCustomer = data.data.find((d) => d.refIdUsers === createId);
                        if (foundCustomer) {
                            setCustomerData(foundCustomer);
                        } else if (pageNum === 1) {
                            // If not found in first page, search in existing list
                            setCustomerData(customerDetails.find((d) => d.refIdUsers === createId));
                        }
                    } else if (id && pageNum === 1 && !isCustomerDeleteRef.current) {
                        const foundCustomer = data.data.find((d) => d._id == id);
                        if (foundCustomer) {
                            setCustomerData(foundCustomer);
                        } else {
                            // If not found in first page, search in existing list
                            setCustomerData(customerDetails.find((d) => d._id == id));
                        }
                    }
                    setServicePageData({
                        totalpages: data?.totalPages,
                        currentPage: data?.currentPage,
                    });
                    setCustomerListLoader(false);
                    setShowLoader(false);
                }
            }
        } catch (error) {
            console.log(error);
            setCustomerListLoader(false);
        } finally {
            setCheckChangeLoader(false);
        }
    };

    // Customer detail fetching function
    const customerDataDetail = async (newId) => {
        setShowLoader(true);

        try {
            let resp;
            if (type != SHOP_IMPORTED) {
                resp = await dispatch(customerDetail(newId || id));
            }
            if (type == SHOP_IMPORTED) {
                resp = await dispatch(importedCustomerDetail(newId || id));
            }

            const customerObject = {
                ...resp,
                fullName: resp?.firstName + ' ' + resp?.lastName,
                refIdUsers: id,
                refIdImport: id,
                _id: resp._id || id, // Ensure _id exists for proper comparison
            };

            setCustomerDetails([customerObject]);
            setCustomerData(customerObject);
            setValue('customer', customerObject);
        } catch (error) {
            console.error('Error fetching customer details:', error);
        } finally {
            setShowLoader(false);
        }
    };

    // Vehicle detail fetching function
    const vehicleDetail = async (newCustomerId, id2) => {
        try {
            setShowLoader(true);
            setVehicleList([]);
            setVehicleLoader(true);
            let data = await dispatch(
                vehicleDetails(10, 1, newCustomerId ? newCustomerId : customerData?.refIdUsers ? customerData?.refIdUsers : customerData?._id)
            );

            if (data) {
                setVehicleList(data);
                if (id2) {
                    let resolvedId = null;

                    if (typeof id2 === 'string') {
                        resolvedId = id2;
                    } else if (typeof id2 === 'object' && id2 !== null) {
                        resolvedId = resolveSelectedVehicleId(data, id2);
                    }

                    if (resolvedId) {
                        setVehicleData(resolvedId);
                        setValue('vehicle', resolvedId);
                        clearErrors('vehicle');
                        const selectedVehicle = Array.isArray(data) ? data.find((v) => v._id === resolvedId) : null;
                        if (selectedVehicle?.source === 'shop_imported') {
                            setEditVehicleData(selectedVehicle);
                            handleVehicleOpen();
                        }
                        return;
                    }
                }

                if (data.length == 0) {
                    handleVehicleOpen();
                } else if (data.length == 1) {
                    setVehicleData(data[0]?._id);
                    setValue('vehicle', data[0]?._id);
                    clearErrors('vehicle');
                    if (data[0].source && data[0].source == 'shop_imported') {
                        setEditVehicleData(data[0]);
                        handleVehicleOpen();
                    }
                } else if (data.length > 1 && !id) {
                    const autocompleteInput = document.getElementById('vehicle-select-autocomplete-appointment');

                    if (autocompleteInput) {
                        autocompleteInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setTimeout(() => {
                            const event = new MouseEvent('mousedown', {
                                bubbles: true,
                                cancelable: true,
                                view: window,
                            });
                            autocompleteInput.dispatchEvent(event);
                            autocompleteInput.focus();
                        }, 100);
                    }
                } else {
                    handleCloseCustomerInfo(null, true);
                }
            }
        } catch (error) {
            console.error('Error fetching vehicle details:', error);
        } finally {
            setVehicleLoader(false);
            setShowLoader(false);
        }
    };

    // Mechanic/Assignee list fetching function
    const mechanicList = async () => {
        setAssigneeLoader(true);
        try {
            let data = await dispatch(mechanicDetailsList());
            setMechanicDetail(data);
            if (data?.length == 1) {
                setMechanicData(data[0]?._id);
                setValue('assignee', data[0]?._id);
            } else {
                const defaultMechanic = data?.find((d) => d.isDefault == true);
                if (defaultMechanic) {
                    setMechanicData(defaultMechanic._id);
                    setValue('assignee', defaultMechanic._id);
                }
            }
        } catch (error) {
            console.error('Error fetching mechanic details:', error);
        } finally {
            setAssigneeLoader(false);
        }
    };

    // Customer input handlers
    async function handleInputChange(newInputValue) {
        setCustomerSearch(newInputValue);
        setCustomerDetails([]);
        setPage(1); // Reset to first page when search changes
        try {
            setCustomerListLoader(true);
            let data = await dispatch(CustomersListBasedOnType(newInputValue, customerTypeData, 1, limits));
            if (data) {
                setCustomerDetails(data.data);
                setServicePageData({
                    totalpages: data?.totalPages,
                    currentPage: data?.currentPage,
                });
                setCustomerListLoader(false);
            }
        } catch (error) {
            console.log(error);
            setCustomerListLoader(false);
        }
    }
    const debouncedHandleInputChange = debounce(handleInputChange, 300);
    const handleInputDebounced = (event, newInputValue) => {
        debouncedHandleInputChange(newInputValue);
    };

    const handleCheckChange = (event) => {
        setCheckChangeLoader(true);
        setCustomerDetails([]);
        setCustomerSearch('');
        setPage(1);
        if (event.target.name && event.target.checked == true) {
            setCustomerTypeData((prevArray) => [...prevArray, event.target.name]);
        } else {
            const filterData = customerTypeData.filter((value) => value !== event.target.name);
            setCustomerTypeData(filterData);
        }
        setState({ ...state, [event.target.name]: event.target.checked });
    };

    const handleBlur = () => {
        if (customerSearch != '') {
            setCustomerSearch('');
            handleInputChange(''); // Clear the search text when the Autocomplete loses focus
        }
    };

    const handleScroll = (e) => {
        const listboxNode = e.currentTarget;
        // Check if scrolled to bottom (with a small threshold for better UX)
        const isScrolledToBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5;

        // Only load more if:
        // 1. Scrolled to bottom
        // 2. Not currently loading
        // 3. There are more pages available
        // 4. Current page is less than total pages
        if (isScrolledToBottom && !customerListLoader && servicePageData.totalpages && page < servicePageData.totalpages) {
            const nextPage = page + 1;
            setPage(nextPage);
            customerList(nextPage, limits);
        }
    };

    // Modal handlers
    // const handleOpenCustomerInfo = () => setOpenCustomerInfo(true);
    const handleOpenCustomerInfo = () => setOpenAddCustomerModal(true);

    const handleCloseCustomerInfo = (resp, check, convertedUser) => {
        // setOpenCustomerInfo(false);
        setOpenAddCustomerModal(false);
        if (convertedUser) {
            const userData = resp.data;
            const customerObject = {
                ...userData,
                fullName: userData?.firstName + ' ' + userData?.lastName,
                createdFrom: SHOP_CREATED,
                refIdUsers: id,
                refIdImport: id,
                _id: userData._id || id, // Ensure _id exists for proper comparison
            };

            setCustomerDetails([customerObject]);
        } else if (resp?.data?._id && !convertedUser) {
            customerDataDetail(resp.data._id);
        } else if (!check) {
            setCustomerData(null);
        }
    };

    const handleVehicleOpen = () => {
        setOpenVehicleModal(true);
        handleClose();
    };

    const handleClose = () => {
        setOpen(false);
    };

    const getShopClosingTime = async (time) => {
        // Cancel previous API call if exists
        if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel('Operation canceled due to new request.');
        }
        cancelTokenSourceRef.current = axios.CancelToken.source();

        try {
            const resp = await getShopClosingTimeApi({
                dropOffDateTime: dayjs(time).utc(),
                cancelToken: cancelTokenSourceRef.current.token,
            });

            // Handle response structure - could be resp.data or resp.data.data
            const shopClosingTime = resp?.data?.shopClosingTime || resp?.shopClosingTime;

            if (shopClosingTime) {
                // Convert API response to local time
                const shopClosingTimeLocal = dayjs(shopClosingTime).tz(localTimezone);

                // Update state and form values
                setPickUpDate(shopClosingTimeLocal);
                setValue('pickUpDate', shopClosingTimeLocal);
                clearErrors('pickUpDate');
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                // Request was canceled, ignore
                console.log('Request canceled:', error.message);
            } else {
                // Handle other errors
                console.error('Error fetching shop closing time:', error);
            }
        }
    };

    const getClosestAllowedMinute = (minute) => {
        let allowedMinutes = [0, 15, 30, 45];
        for (let i = 0; i < allowedMinutes.length; i++) {
            if (minute <= allowedMinutes[i]) return allowedMinutes[i];
        }
        return allowedMinutes[0];
    };

    const disablePrevDates = (startDate) => {
        const startSeconds = Date.parse(startDate);
        return (date) => {
            return Date.parse(date) < startSeconds;
        };
    };

    const queryClient = useQueryClient();

    // TanStack Query mutation for creating appointment
    const createAppointmentMutation = useCreateAppointment();

    // Fetch all services on mount (for service section)
    const fetchAllService = async () => {
        try {
            const response = await dispatch(
                getAllShopServiceApi({
                    pagination: false,
                    limit: 2000,
                    sortBy: 'name',
                    sortType: 'ASC',
                })
            );
            localStorage.setItem('allServices', JSON.stringify(response?.data || []));
            return response?.data || [];
        } catch (error) {
            console.error('Error fetching services:', error);
            return [];
        }
    };

    // Initialize form values and shop closing time on mount
    useEffect(() => {
        reset({
            noteForCustomer: '',
        });
        setServiceList([{ _id: 'select' }]);
        setVehicleData('');
        setDropOffDate(null);
        setPickUpDate(null);
        setCustomerOnSite(null);
        setMechanicData('');
        mechanicList();

        const roundedTime = dayjs();
        setDropOffDate(roundedTime);
        setValue('dropOffDate', roundedTime);
        getShopClosingTime(roundedTime.format('YYYY-MM-DDTHH:mm'));
        // Reset date picker usage ref on mount
        datePickerWasUsedRef.current = false;

        if (id && type) {
            customerDataDetail();
        } else {
            setValue('customer', null);
            setCustomerData(null);
            setCustomerDetails([]);
        }
        setCustomerTypeData(['shop_created', 'apex_auto_app', 'shop_imported']);
        setState({
            shop_created: true,
            apex_auto_app: true,
            shop_imported: true,
        });
        fetchAllService();
    }, [location.pathname]);

    // Fetch customer list when customer type data changes
    useEffect(() => {
        if (customerTypeData && customerTypeData.length > 0) {
            if (!id && !type) {
                setPage(1); // Reset to first page when customer type changes
                customerList(1, limits);
            }
        }
    }, [customerTypeData]);

    // Fetch vehicle details when customer is selected
    useEffect(() => {
        if (customerData) {
            if (isCustomerDeleteRef.current) {
                isCustomerDeleteRef.current = false;
                return;
            }

            if (customerData?.createdFrom == USER_TYPES.SHOP_IMPORTED) {
                setCustomerCreatedFrom(USER_TYPES.SHOP_IMPORTED);
                setIsEditCustomer(true);
                setOpenAddCustomerModal(true);
            } else {
                setCustomerCreatedFrom(USER_TYPES.SHOP_CREATED);
            }

            if (customerData && customerData?.createdFrom != USER_TYPES.SHOP_IMPORTED) {
                if (skipVehicleDetailRef.current) {
                    skipVehicleDetailRef.current = false;
                    return;
                }
                vehicleDetail();
            }
        } else {
            // Clear vehicle data when customer is cleared
            setVehicleData('');
            setVehicleList([]);
            setValue('vehicle', null);
        }
    }, [customerData]);

    // Set vehicle when new vehicle is created
    useEffect(() => {
        if (Array.isArray(vehicleList) && vehicleList.length > 0 && newVehicleCreatedId) {
            setVehicleData(newVehicleCreatedId);
            setValue('vehicle', newVehicleCreatedId);
            setVehicleCreatedId('');
        }
    }, [vehicleList]);

    const handleCancel = () => {
        navigate(-1);
    };

    const handleConfirm = async (value) => {
        try {
            const customerValue = getValues('customer');
            const customerId = customerValue?.refIdUsers || customerValue?._id;

            if (!customerId) {
                setError('customer', {
                    type: 'custom',
                    message: 'Customer is required',
                });
                toast.error('Please select a customer to continue.');
                return;
            }

            const vehicleId = getValues('vehicle');

            if (!vehicleId) {
                setError('vehicle', {
                    type: 'custom',
                    message: 'Vehicle is required',
                });
                toast.error('Please select a vehicle to continue.');
                return;
            }

            // Validate drop off date
            if (!dayjs(dropOffDate).isValid()) {
                setError('dropOffDate', {
                    type: 'custom',
                    message: 'Invalid date',
                });
                toast.error('Please select a valid drop-off date.');
                return;
            }

            // Get assignee from form (form is source of truth)
            const assigneeValue = getValues('assignee');
            if (!assigneeValue) {
                setError('assignee', {
                    type: 'custom',
                    message: 'Assign To is required',
                });
                toast.error('Please select an assignee.');
                return;
            }

            // Get customer ID - handle both object and ID string
            // const customerValue = getValues('customer');
            // const customerId = customerData?.refIdUsers || customerData?._id || (customerValue?._id ? customerValue._id : customerValue) || undefined;

            // Build submit data payload
            let submitData = {
                customer: customerId,
                vehicle: vehicleData || value?.vehicle || undefined,
                assignee: [assigneeValue],
            };

            if (customerOnSite && customerOnSite !== '') {
                submitData.waitingOnSite = customerOnSite;
            }

            if (dropOffDate) {
                submitData.dropOffDateTime = dropOffDate.toISOString();
            }

            if (pickUpDate) {
                submitData.pickupDateTime = pickUpDate.toISOString();
            }

            if (value?.contactPrefrence) {
                submitData.contactPreferences = value?.contactPrefrence;
            }

            // if (value?.noteForCustomer) {
            //     submitData.noteForCustomer = value.noteForCustomer.trim();
            // }
            const note = getValues('noteForCustomer');

            if (note && note.trim() !== '') {
                submitData.noteForCustomer = note.trim();
            }

            // Validate services
            if (serviceList.length > 0 && serviceList[0]._id !== 'select') {
                let serviceArray = [];
                serviceList.forEach((item) => {
                    if (item?._id !== 'select') {
                        serviceArray.push({
                            serviceId: item?.serviceSubCategory?._id,
                            description: item?.description ?? '',
                        });
                    }
                });
                submitData.services = serviceArray;
            } else {
                toast.error('Please add at least one service to continue.');
                return;
            }

            // Check if selected time is within business hours
            // Skip check if:
            // 1. User already saved from date picker (datePickerWasUsedRef.current is true)
            // 2. User clicked "continue anyway" (bypassBusinessHoursCheckRef.current is true)
            if (!bypassBusinessHoursCheckRef.current && !datePickerWasUsedRef.current && !isTimeWithinBusinessHours(dropOffDate, processedSlotsFull)) {
                // Open the dialog for times outside business hours
                setOpenOutsideBusinessHoursDialog(true);
                return;
            }

            // Reset bypass ref after check
            bypassBusinessHoursCheckRef.current = false;
            setShowLoader(true);

            // Call API with skipConfirmation params
            const result = await createAppointmentMutation.mutateAsync({
                params: {
                    dontSendFCMNotificationToCustomer: skipConfirmation,
                    dontSendEmailToCustomer: skipConfirmation,
                    smsNotificationDisabledForCustomer: skipConfirmation,
                },
                payload: submitData,
            });

            // Handle response - API returns { status: true, data: {...} } or { data: {...} }
            if (result?.status && result?.data) {
                // Invalidate appointment queries
                queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
                setShowLoader(false);
                toast.success('Appointment created successfully');

                setTimeout(() => {
                    if (getPreviousPath()?.split('/')?.includes('calendar')) {
                        clearAllPaths();
                        navigate(-1);
                    } else {
                        navigate('/appointment/today');
                    }
                }, 1000);
            } else if (result?.data) {
                // Alternative response structure
                queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
                setShowLoader(false);
                toast.success('Appointment created successfully');

                setTimeout(() => {
                    if (getPreviousPath()?.split('/')?.includes('calendar')) {
                        clearAllPaths();
                        navigate(-1);
                    } else {
                        navigate('/appointment/today');
                    }
                }, 1000);
            } else {
                setShowLoader(false);
                toast.error('Failed to create appointment. Please try again.');
            }
        } catch (error) {
            console.error('Error creating appointment:', error);
            setShowLoader(false);
            toast.error('Failed to create appointment. Please try again.');
        }
    };

    return (
        <form onSubmit={handleSubmit(handleConfirm)} style={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    width: '100%',
                    minHeight: 'calc(100vh - 64px)',
                }}
            >
                <Box
                    padding={'24px'}
                    sx={{
                        width: '100%',
                        flex: 1,
                    }}
                >
                    <Header />

                    {/* MainBodyGrid - Two Column Layout */}
                    <Grid container margin={'16px 0'}>
                        <Grid item container spacing={3} xs={12}>
                            {/* Left Side - Appointment Details Section */}
                            <Grid
                                item
                                container
                                xs={12}
                                sm={12}
                                md={12}
                                lg={isOpenSideBar && isBelow1440 ? 12 : 4}
                                spacing={3}
                                style={{ height: 'max-content' }}
                            >
                                <AppointmentDetailsAccordion
                                    control={control}
                                    errors={errors}
                                    setValue={setValue}
                                    clearErrors={clearErrors}
                                    setError={setError}
                                    getValues={getValues}
                                    customerDetails={customerDetails}
                                    customerData={customerData}
                                    setCustomerData={setCustomerData}
                                    customerListLoader={customerListLoader}
                                    customerTypeData={customerTypeData}
                                    setCustomerTypeData={setCustomerTypeData}
                                    state={state}
                                    setState={setState}
                                    handleCheckChange={handleCheckChange}
                                    handleInputDebounced={handleInputDebounced}
                                    handleBlur={handleBlur}
                                    handleScroll={handleScroll}
                                    servicePageData={servicePageData}
                                    page={page}
                                    setPage={setPage}
                                    customerList={customerList}
                                    limits={limits}
                                    customerCreatedFrom={customerCreatedFrom}
                                    setCustomerCreatedFrom={setCustomerCreatedFrom}
                                    handleOpenCustomerInfo={handleOpenCustomerInfo}
                                    openAddCustomerModal={openAddCustomerModal}
                                    setOpenAddCustomerModal={setOpenAddCustomerModal}
                                    skipConfirmation={skipConfirmation}
                                    setSkipConfirmation={setSkipConfirmation}
                                    vehicleList={vehicleList}
                                    vehicleData={vehicleData}
                                    setVehicleData={setVehicleData}
                                    vehicleLoader={vehicleLoader}
                                    //  setVehicleSourceData={setVehicleSourceData}
                                    handleVehicleOpen={handleVehicleOpen}
                                    openVehicleModal={openVehicleModal}
                                    setOpenVehicleModal={setOpenVehicleModal}
                                    editVehicleData={editVehicleData}
                                    setEditVehicleData={setEditVehicleData}
                                    mechanicDetail={mechanicDetail}
                                    mechanicData={mechanicData}
                                    setMechanicData={setMechanicData}
                                    assigneeLoader={assigneeLoader}
                                    customerSearch={customerSearch}
                                    //   openCustomerInfo={openCustomerInfo}
                                    // vehicleInfoOpen={vehicleInfoOpen}
                                    handleCloseCustomerInfo={handleCloseCustomerInfo}
                                    //  handleVehicleInfoClose={handleVehicleInfoClose}
                                    handleVehicleInfoClose={handleClose}
                                    dropOffDate={dropOffDate}
                                    setDropOffDate={setDropOffDate}
                                    pickUpDate={pickUpDate}
                                    setPickUpDate={setPickUpDate}
                                    getShopClosingTime={getShopClosingTime}
                                    isFirstSelection={isFirstSelection}
                                    setIsFirstSelection={setIsFirstSelection}
                                    getClosestAllowedMinute={getClosestAllowedMinute}
                                    disablePrevDates={disablePrevDates}
                                    customerOnSite={customerOnSite}
                                    setCustomerOnSite={setCustomerOnSite}
                                    setIsEditCustomer={setIsEditCustomer}
                                    id={id}
                                    isOpenSideBar={isOpenSideBar}
                                    shopConfigData={shopConfigData}
                                    triggerOpenDatePicker={triggerOpenDatePicker}
                                    datePickerWasUsedRef={datePickerWasUsedRef}
                                    shopId={SHOP_ID}
                                />
                                <AdditionalDetailsAccordion
                                    control={control}
                                    errors={errors}
                                    customerOnSite={customerOnSite}
                                    setCustomerOnSite={setCustomerOnSite}
                                />
                            </Grid>

                            {/* Right Side - Add Services Section */}
                            <Grid item xs={12} sm={12} md={12} lg={isOpenSideBar && isBelow1440 ? 12 : 8}>
                                <ServicesSection serviceList={serviceList} setServiceList={setServiceList} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
                <Footer onCancel={handleCancel} onConfirm={handleConfirm} />
            </Box>
            <Dialog fullScreen={false} open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
            <Dialog fullScreen={false} open={checkChangeLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            {openVehicleModal && (
                <Suspense fallback={<CircularProgress />}>
                    <VehicleModalV4
                        open={openVehicleModal}
                        enableLicenseDecoding={false}
                        mode={
                            editVehicleData?.source === USER_TYPES.SHOP_IMPORTED
                                ? editVehicleData?.source
                                : editVehicleData
                                  ? 'update_shop_created_vehicle'
                                  : 'create'
                        }
                        customerId={customerData?.refIdUsers || customerData?._id}
                        vehicleId={editVehicleData?._id}
                        vehicleData={
                            editVehicleData
                                ? {
                                      _id: editVehicleData?._id,
                                      vin: editVehicleData?.vin || '',
                                      year: editVehicleData?.year || editVehicleData?.vehicleYear || '',
                                      vehicleMakeId: editVehicleData?.vehicleMake?._id,
                                      vehicleModelId: editVehicleData?.vehicleModel?._id,
                                      vehicleModelTrimId: editVehicleData?.vehicleModelTrim?._id,
                                      licenseNumber: editVehicleData?.licenseNumber || editVehicleData?.license || '',
                                      provinceId: editVehicleData?.provinceId || editVehicleData?.province?._id,
                                      fleetUnitNumber: editVehicleData?.fleetUnitNumber || '',
                                  }
                                : null
                        }
                        isFleetCustomer={customerData?.shopCustomer?.isFleetCustomer || customerData?.isFleetCustomer || false}
                        onClose={() => {
                            setOpenVehicleModal(false);
                            setEditVehicleData(null);
                        }}
                        onSuccess={(updatedVehicle) => {
                            const customerIdForVehicle = customerData?.refIdUsers || customerData?._id;
                            const vehicleIdToSelect = updatedVehicle?._id ?? editVehicleData?._id;
                            if (customerData) {
                                vehicleDetail(customerIdForVehicle, vehicleIdToSelect);
                            }

                            setOpenVehicleModal(false);
                            setEditVehicleData(null);
                        }}
                    />
                </Suspense>
            )}

            {openAddCustomerModal && (
                <Suspense fallback={<CircularProgress />}>
                    <AddCustomerModalV4
                        openModal={openAddCustomerModal}
                        customerId={
                            isEditCustomer ? getValues()?.customer?.refIdImport || getValues()?.customer?.refIdUsers || getValues()?.customer?._id : null
                        }
                        customerType={getValues()?.customer?.createdFrom || USER_TYPES.shop_imported}
                        onClose={() => {
                            setIsEditCustomer(false);
                            setOpenAddCustomerModal(false);
                        }}
                        onSubmitModal={async (resp, isUserDelete) => {
                            if (isUserDelete) {
                                isCustomerDeleteRef.current = true;
                                setCustomerDetails([]);
                                setValue('customer', '');
                                setCustomerData('');
                                customerList(1, limits);

                                setOpenAddCustomerModal(false);

                                if (id && type === USER_TYPES.SHOP_IMPORTED) {
                                    navigate(`/appointment/new-appointment`, { replace: true });
                                    setCustomerCreatedFrom(USER_TYPES.SHOP_CREATED);
                                    return;
                                }
                            }
                            if (id && type === USER_TYPES.SHOP_IMPORTED) {
                                setIsEditCustomer(false);
                                setOpenAddCustomerModal(false);
                                navigate(`/appointment/new-appointment/${resp._id}/${USER_TYPES.SHOP_CREATED}`, { replace: true });
                                // await customerDataDetail(resp._id);
                                setTimeout(() => {
                                    const selectedVehicle = resp?.selectedVehicle || resp?.selectedVehicleId || null;
                                    if (selectedVehicle) {
                                        vehicleDetail(resp._id, selectedVehicle);
                                    }
                                }, 2000);
                                return;
                            }

                            // Handle customer creation/update response
                            if (resp?._id) {
                                // update the react hook field accordingly
                                const selectedVehicle = resp?.selectedVehicle || null;
                                const selectedVehicleId = resp?.selectedVehicleId || null;

                                // Invalidate customer detail query cache to ensure fresh data
                                queryClient.invalidateQueries({ queryKey: customerKeys.createdDetail(resp._id) });
                                queryClient.invalidateQueries({ queryKey: customerKeys.lists() });

                                if (selectedVehicle || selectedVehicleId) {
                                    skipVehicleDetailRef.current = true;
                                }

                                // Fetch full customer details and set in state
                                await customerDataDetail(resp._id);
                                setOpenAddCustomerModal(false);

                                const vehicleIdentifier = selectedVehicle || selectedVehicleId;
                                if (vehicleIdentifier) {
                                    vehicleDetail(resp?._id, vehicleIdentifier);
                                }
                            } else {
                                setOpenAddCustomerModal(false);
                                setIsEditCustomer(false);
                            }
                        }}
                    />
                </Suspense>
            )}

            <CommonDialog
                open={openOutsideBusinessHoursDialog}
                onClose={() => {
                    setOpenOutsideBusinessHoursDialog(false);
                    // Trigger date picker to open
                    setTriggerOpenDatePicker((prev) => prev + 1);
                }}
                dialogClaassName="commonDialog commonDialog__560"
                dialogTitle={isSelectedDateAvailable ? '⚠️ Outside Drop-Off Availability' : '⚠️ Shop Closed'}
                dialogSubtitle={
                    isSelectedDateAvailable
                        ? `The selected drop-off time (${businessHoursInfo.selectedTime}) is outside the shop's configured drop-off availability (${businessHoursInfo.businessDay} ${businessHoursInfo.openTime} – ${businessHoursInfo.closeTime}).`
                        : `The selected drop-off time (${businessHoursInfo.selectedTime}) is on a day the shop is closed (${businessHoursInfo.businessDay}).`
                }
                dialogSubtitle2={'Would you like to adjust time or continue? '}
                cancelButtonWidth={'143px'}
                cancelButtonVariant={'outlined'}
                cancelButtonText={'adjust time'}
                saveButtonType={'primary'}
                saveButtonVariant={'contained'}
                saveButtonText={'continue anyway'}
                saveButtonWidth={'188px'}
                onSave={async () => {
                    setOpenOutsideBusinessHoursDialog(false);
                    // Set bypass ref to true to skip business hours check
                    bypassBusinessHoursCheckRef.current = true;
                    // Trigger form submission
                    handleSubmit(handleConfirm)();
                }}
            />
        </form>
    );
};

export default NewAppointmentV4;
